import { useEffect, useId, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useAppDispatch } from '@/app/store/hooks';
import { logoutStart } from '../../common/store/authSlice';
import { ChangePasswordForm } from './ChangePasswordForm';
import './firstLoginPasswordDialog.css';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])';

interface ChangePasswordDialogProps {
  /**
   * First sign-in: no close control, Escape and the backdrop do nothing, and Sign out is the only other way
   * out. From the profile menu the user may close it with the X button or Escape -- never by clicking the
   * backdrop.
   */
  mandatory?: boolean;
  /** Profile menu only: the user closed the dialog. */
  onClose?: () => void;
  /**
   * After the platform has accepted the new password. Return `true` to stay open with "Password updated
   * successfully." and a cleared form (the profile menu); first sign-in moves on to the sign-in page instead.
   */
  onChanged: () => boolean | undefined;
}

/**
 * Change Password, as a dialog over the current page -- the one place it is shown, from the profile menu and
 * on first sign-in alike.
 *
 * The form is `ChangePasswordForm`, so the fields, rules and messages are the same in both. While it is open
 * the application behind it is `inert` -- out of reach of the pointer, Tab and screen readers -- and focus is
 * held inside; on close, focus goes back to where it was.
 */
export function ChangePasswordDialog({ mandatory = false, onClose, onChanged }: ChangePasswordDialogProps) {
  const dispatch = useAppDispatch();
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const root = document.getElementById('root');
    root?.setAttribute('inert', '');
    root?.setAttribute('aria-hidden', 'true');
    panel.current?.querySelector<HTMLElement>('input')?.focus();
    return () => {
      root?.removeAttribute('inert');
      root?.removeAttribute('aria-hidden');
      opener?.focus();
    };
  }, []);

  const close = () => {
    if (!mandatory) onClose?.();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    if (event.key !== 'Tab' || !panel.current) return;
    const focusable = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  /*
    No backdrop close. A click outside the panel does nothing, so a stray click cannot throw away what has
    been typed: the dialog closes only through its X button, or Escape as the keyboard equivalent.
  */

  let footer: ReactNode = null;
  if (mandatory) {
    footer = (
      <button
        type="button"
        className="pw-recovery__back pw-recovery__back-button"
        onClick={() => dispatch(logoutStart())}
      >
        Sign out
      </button>
    );
  }

  return createPortal(
    <div className="pw-dialog__mask" role="presentation">
      <div
        ref={panel}
        className="pw-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
      >
        {mandatory ? null : (
          <button
            type="button"
            className="pw-dialog__close"
            aria-label="Close"
            onClick={close}
          >
            <i className="pi pi-times" aria-hidden="true" />
          </button>
        )}
        <div className="pw-dialog__head">
          <span className="pw-dialog__icon" aria-hidden="true">
            <i className="pi pi-lock" />
          </span>
          <h2 className="pw-dialog__title" id={titleId}>
            Change Password
          </h2>
          <p className="pw-dialog__subtitle">
            {mandatory
              ? 'For your security, set a new password before you continue.'
              : 'Set a new password and confirm it.'}
          </p>
        </div>

        <ChangePasswordForm
          idPrefix={mandatory ? 'pw-first' : 'pw-change'}
          onChanged={onChanged}
          footer={footer}
        />
      </div>
    </div>,
    document.body,
  );
}
